import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { DT, DS2 } from '../../theme/designTokens';
import { useFriends } from '../../context/FriendContext';
import { useIsMobile } from '../../hooks/useBreakpoint';
import { PlayerIdentity } from '../../components/ui/PlayerIdentity';
import { ImageLightbox } from '../../components/ui/ImageLightbox';
import { DirectMessageBubbles } from '../../components/chat/dm/DirectMessageBubbles';
import { DirectMessageComposer } from '../../components/chat/dm/DirectMessageComposer';
import { useTranslation } from '../../i18n/react';

function BackIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M15 5l-7 7 7 7" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface Props {
  friendId: number;
  friendName: string;
  friendCountryCode?: string | null;
  friendAvatarKey?:   string | null;
  onBack?: () => void;
}

export function MessageThread({ friendId, friendName, friendCountryCode, friendAvatarKey, onBack }: Props) {
  const isMobile = useIsMobile();
  const { t } = useTranslation('messages');
  const { t: tc } = useTranslation('common');
  const { threads, openThread, closeThread, sendMessage, conversations, loadConversations } = useFriends();
  const [loaded, setLoaded]   = useState(false);
  const [fullscreen, setFullscreen]   = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const messages = threads[friendId] ?? [];

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    void openThread(friendId).finally(() => { if (!cancelled) setLoaded(true); });
    return () => {
      cancelled = true;
      closeThread();
    };
  }, [friendId, openThread, closeThread]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length]);

  async function handleSend(body: string, image: File | undefined) {
    await sendMessage(friendId, body, image);
    if (!conversations.some(c => c.friend.id === friendId)) void loadConversations();
  }

  const containerStyle: CSSProperties = fullscreen
    ? { position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', flexDirection: 'column', minHeight: 0, background: DT.color.pageBg }
    : { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0, background: DT.color.pageBg };

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: isMobile ? '14px 16px' : '18px 24px', borderBottom: `1px solid ${DS2.color.cardBorder}`, flexShrink: 0 }}>
        {onBack && !fullscreen && (
          <button type="button" aria-label={t('backToConversations')} onClick={onBack} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}>
            <BackIcon />
          </button>
        )}
        <PlayerIdentity
          name={friendName}
          countryCode={friendCountryCode}
          avatarKey={friendAvatarKey}
          size="standard"
          flagLoading="eager"
          nameStyle={{ fontSize: 16, fontWeight: 700, fontFamily: DS2.font.heading, color: '#FFFFFF' }}
        />
      </div>

      <div ref={listRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: isMobile ? '16px' : '20px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {!loaded ? (
          <div style={{ margin: 'auto', color: '#8A8A8A', fontSize: 13, fontFamily: DS2.font.body }}>{tc('status.loading')}</div>
        ) : messages.length === 0 ? (
          <div style={{ margin: 'auto', textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: 14, fontFamily: DS2.font.body, color: '#CCCCCC' }}>{t('noMessagesYet')}</p>
            {/* Whole sentence; the friend's name is interpolated verbatim. */}
            <p style={{ margin: '6px 0 0', fontSize: 12, fontFamily: DS2.font.body, color: '#8A8A8A' }}>{t('sayHello', { name: friendName })}</p>
          </div>
        ) : (
          <DirectMessageBubbles messages={messages} friendId={friendId} onImageClick={setLightboxUrl} />
        )}
      </div>

      <div style={{ padding: isMobile ? '10px 16px 14px' : '12px 24px 18px', borderTop: `1px solid ${DS2.color.cardBorder}`, flexShrink: 0 }}>
        <DirectMessageComposer
          onSend={handleSend}
          allowFullscreen
          fullscreen={fullscreen}
          onToggleFullscreen={() => setFullscreen(f => !f)}
        />
      </div>

      {lightboxUrl && <ImageLightbox src={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
    </div>
  );
}
